<template>
    <PageHeader :title="pageTitle" @back="showDeleteLobbyModal = true" />

    <div v-if="isLoadingMatch" class="flex flex-1 items-center justify-center">
        <div class="text-center text-white">
            <p class="text-lg font-bold">{{ $t('lobby.loading') }}</p>
        </div>
    </div>

    <template v-else>
        <div class="flex flex-col gap-6 pb-24">
            <!-- Read-only settings summary -->
            <div class="px-4">
                <Panel size="full">
                    <div class="flex items-center justify-between pb-4">
                        <div class="flex items-center gap-3">
                            <Avatar
                                v-if="creatorProfile?.avatar"
                                :src="creatorProfile.avatar"
                                :alt="creatorProfile.tag_name"
                                :size="10"
                            />
                            <h3 class="text-lg font-bold uppercase">{{ lobbyName }}</h3>
                        </div>
                    </div>
                    <div class="flex flex-wrap gap-3">
                        <div
                            v-for="entry in settingsSummary"
                            :key="entry.key"
                            class="border-stroke-1 flex flex-col bg-card-2 p-3 gap-1"
                        >
                            <span class="text-titles text-xs capitalize">{{ entry.label }}</span>
                            <span class="text-sm font-bold capitalize leading-3">{{ entry.value }}</span>
                        </div>
                    </div>
                </Panel>
            </div>

            <!-- Your dart setup (editable by every participant) -->
            <div class="px-4" v-if="!currentGame.hideDartSetup">
                <Panel size="full">
                    <h3 class="pb-4 text-lg font-bold uppercase">{{ $t('lobby.your_setup') }}</h3>
                    <SetupsSelector
                        :model-value="matchStore.setup.dart_setup_id"
                        @update:model-value="handleSetupChange"
                    />
                </Panel>
            </div>

            <!-- Players -->
            <div class="px-4">
                <Panel size="full">
                    <div class="flex items-center justify-between pb-4">
                        <h3 class="text-lg font-bold uppercase">{{ $t('lobby.players') }}</h3>
                        <span data-testid="lobby-party-size" class="text-titles text-sm font-bold">{{ partySize }}</span>
                    </div>

                    <PlayerSelectionSection v-if="isHost" />

                    <template v-else>
                        <div class="flex flex-col items-stretch gap-3 md:max-w-2xl">
                            <PlayerCard
                                v-for="matchProfile in sortedProfiles"
                                :key="matchProfile.id"
                                :player="matchProfile"
                                :current-user-id="authStore.profile?.id"
                                :show-remove-button="false"
                                horizontal
                                :badge="playerBadge(matchProfile)"
                                :badge-variant="playerBadgeVariant(matchProfile)"
                                :status="playerStatus(matchProfile)"
                            />
                        </div>
                        <p
                            v-if="!hasMatchStarted"
                            data-testid="lobby-waiting-message"
                            class="text-titles pt-4 text-center text-sm"
                        >
                            {{ $t('lobby.waiting_for_host') }}
                        </p>
                    </template>
                </Panel>
            </div>

            <!-- Game explanation -->
            <div class="px-4">
                <Panel size="full">
                    <div class="pb-4 text-sm">
                        <p v-html="$tm('game_content.' + currentGame.name + '.description')[0]"></p>
                    </div>

                    <button
                        class="flex items-center gap-2 text-sm font-bold text-red-600"
                        @click="openExplanation(currentGameContent.type)"
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M7.50059 4.21606C13.4983 0.758474 21 5.07897 21 12.0003C21 15.2121 19.284 18.1793 16.4994 19.7846L16.4994 19.7846C10.5017 23.2422 3 18.9217 3 12.0003C3 8.78849 4.71602 5.82135 7.50058 4.21607L7.50059 4.21606ZM19 12.0003C19 6.62318 13.1684 3.25723 8.49945 5.94876L8.00002 5.08241L8.49945 5.94876C6.33348 7.19744 5 9.50447 5 12.0003C5 17.3775 10.8316 20.7434 15.5005 18.0519C17.6665 16.8032 19 14.4962 19 12.0003ZM13 7.00032L13 8.50032L11 8.50032L11 7.00032L13 7.00032ZM13 10.0003V17.0003H11V10.0003H13Z"
                                fill="#FF2C2C"
                            />
                        </svg>
                        <span>{{ $t('game_explanation.read_more') }}</span>
                    </button>
                </Panel>
            </div>

            <!-- Host: start controls -->
            <div
                v-if="isHost"
                class="bg-card border-stroke-1 fixed right-0 bottom-0 left-0 z-10 border-t p-4"
                :style="{ paddingBottom: 'calc(1rem + var(--safezone-bottom, 0px))' }"
            >
                <div
                    v-if="(showValidationError || hasPendingPlayers) && currentValidationMessage"
                    class="mb-4"
                >
                    <div
                        class="rounded border border-red-800 bg-red-900/20 p-2 text-center text-sm text-red-400"
                    >
                        {{ currentValidationMessage }}
                    </div>
                </div>
                <DiagonalCutButton
                    class="w-full"
                    variant="primary"
                    data-testid="lobby-start-button"
                    @click="nextStep"
                    :disabled="!canProceed || isSaving"
                    >{{ isSaving ? $t('lobby.starting') : $t('lobby.start_game') }}</DiagonalCutButton
                >
            </div>
        </div>
    </template>

    <Modal
        :is-visible="showDeleteLobbyModal"
        :on-confirm="deleteLobby"
        :on-decline="() => (showDeleteLobbyModal = false)"
        :is-loading="isDeleting"
        :confirm-text="$t('lobby.delete_lobby')"
    >
        <h2 class="font-title mb-2 text-base leading-normal font-extrabold text-white uppercase">
            {{ $t('lobby.delete_lobby_title') }}
        </h2>
        <p class="mt-4 text-sm leading-normal font-normal text-white">
            {{ $t('lobby.delete_lobby_body') }}
        </p>
    </Modal>
</template>

<script>
import { mapStores } from 'pinia'
import { ANALYTICS_EVENTS } from '@/constants/analyticsEvents.ts'
import { useMatchStore } from '@/stores/match.js'
import { useAuthStore } from '@/stores/auth.js'
import PageHeader from '@/components/PageHeader.vue'
import Modal from '@/components/Modal.vue'
import Panel from '@/components/layout/Panel.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import PlayerSelectionSection from '@/components/players/PlayerSelectionSection.vue'
import PlayerCard from '@/components/players/PlayerCard.vue'
import Avatar from '@/components/Avatar.vue'
import SetupsSelector from '@/components/ui/setups/SetupsSelector.vue'
import { useTeamManagement } from '@/composables/useTeamManagement.js'
import { usePlayerValidation } from '@/composables/usePlayerValidation.js'
import { formatSettingValue } from '@/utils/settingsHelpers.js'
import { matchTypeName, MATCH_TYPES } from '@/constants/matchTypes.js'
import x01Game from '@/services/games/1_x01'
import cricketGame from '@/services/games/2_cricket'
import camelraceGame from '@/services/games/3_camelrace'
import killerGame from '@/services/games/4_killer'
import segmentsTrainingGame from '@/services/games/5_segments'

const GAME_SERVICES = {
    1: x01Game,
    3: cricketGame,
    4: killerGame,
    7: camelraceGame,
    8: segmentsTrainingGame,
}

// i18n keys for settings-summary labels; unknown keys fall back to the raw key.
const SETTING_LABEL_KEYS = {
    win_type: 'match_settings.win_type_label',
    starting_score: 'match_invite.starting_score',
    sets_to_win: 'match_settings.sets',
    legs_to_win: 'match_settings.legs',
    check_in: 'lobby.setting_labels.check_in',
    check_out: 'lobby.setting_labels.check_out',
    darts_per_turn: 'match_settings.darts_per_turn',
    lower_bound: 'match_settings.variant',
    starting_lives: 'match_settings.starting_lives',
    finish_line_score: 'match_settings.finish_line',
    segment_mode: 'lobby.setting_labels.segment_mode',
    hits_per_target: 'match_settings.hits_per_target',
    points_mode: 'lobby.setting_labels.points_mode',
    points_ceiling: 'match_settings.points_ceiling',
    rounds: 'match_settings.rounds',
    target_order: 'lobby.setting_labels.target_order',
    include_bulls: 'match_settings.include_bulls',
}

export default {
    name: 'LobbyView',
    components: {
        PageHeader,
        Modal,
        Panel,
        DiagonalCutButton,
        PlayerSelectionSection,
        PlayerCard,
        Avatar,
        SetupsSelector,
    },
    setup() {
        const teamManagement = useTeamManagement()
        const playerValidation = usePlayerValidation(teamManagement)

        return {
            ...teamManagement,
            ...playerValidation,
            canProceedPlayers: playerValidation.canProceed,
            playerValidationMessage: playerValidation.currentValidationMessage,
        }
    },
    data() {
        return {
            isLoadingMatch: false,
            isSaving: false,
            isLeaving: false,
            showDeleteLobbyModal: false,
            isDeleting: false,
        }
    },
    computed: {
        ...mapStores(useMatchStore, useAuthStore),

        isHost() {
            return this.matchStore.setup.creator_profile_id === this.authStore.profile?.id
        },

        pageTitle() {
            return this.matchStore.isRemotePlay
                ? this.$t('lobby.online_title')
                : this.$t('lobby.local_title')
        },

        currentGame() {
            return GAME_SERVICES[this.matchStore.setup.matchtype_id] || x01Game
        },

        currentGameContent() {
            return this.currentGame.content || x01Game.content
        },

        creatorProfile() {
            return (this.matchStore.setup.profiles || []).find(
                (p) => p.profile_id === this.matchStore.setup.creator_profile_id,
            )
        },

        lobbyName() {
            const gameName = matchTypeName(
                this.matchStore.setup.matchtype_id,
                this.$t('lobby.default_game_name'),
            )
            const gameType = this.matchStore.isRemotePlay
                ? this.$t('lobby.game_type_online')
                : this.$t('lobby.game_type_local')
            return this.creatorProfile
                ? this.$t('lobby.name_with_host', {
                      host: this.creatorProfile.tag_name,
                      type: gameType,
                      game: gameName,
                  })
                : this.$t('lobby.name_without_host', { type: gameType, game: gameName })
        },

        partySize() {
            const count = (this.matchStore.setup.profiles || []).length
            return `${count}/${this.effectiveMaxPlayers}`
        },

        /**
         * Read-only view of the chosen settings. Only scalar values are shown —
         * structured settings (e.g. segment target lists) don't summarize well.
         */
        settingsSummary() {
            const settings = this.matchStore.setup.settings || {}
            const fields = (this.currentGame.form || []).flatMap((section) => section.fields || [])
            const entries = Object.entries(settings)
                .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
                .map(([key, value]) => {
                    const option = fields
                        .find((field) => field.key === key)
                        ?.options?.find((o) => o.value === value)
                    return {
                        key,
                        label: SETTING_LABEL_KEYS[key]
                            ? this.$t(SETTING_LABEL_KEYS[key])
                            : key.replace(/_/g, ' '),
                        value:
                            typeof value === 'boolean'
                                ? value
                                    ? this.$t('lobby.yes')
                                    : this.$t('lobby.no')
                                : option?.labelKey
                                  ? this.$t(option.labelKey)
                                  : formatSettingValue(key, value),
                    }
                })

            entries.push({
                key: 'include_in_stats',
                label: this.$t('lobby.include_stats_label'),
                value: this.matchStore.setup.include_in_stats ? this.$t('lobby.yes') : this.$t('lobby.no'),
            })

            return entries
        },

        sortedProfiles() {
            const profiles = this.matchStore.setup.profiles || []
            const creatorProfileId = this.matchStore.setup.creator_profile_id

            if (!creatorProfileId) return profiles

            return [...profiles].sort((a, b) => {
                if (a.profile_id === creatorProfileId) return -1
                if (b.profile_id === creatorProfileId) return 1
                return 0
            })
        },

        isSoloGame() {
            return this.readyPlayers.length === 1
        },

        isCamelRace() {
            return this.matchStore.setup.matchtype_id === MATCH_TYPES.CAMELRACE
        },

        pendingPlayers() {
            return (this.matchStore.setup.profiles || []).filter((p) => !p.ready)
        },

        hasPendingPlayers() {
            return this.pendingPlayers.length > 0
        },

        pendingPlayersMessage() {
            if (!this.hasPendingPlayers) return ''
            const count = this.pendingPlayers.length
            return count === 1
                ? this.$t('lobby.pending_players_singular', { n: count })
                : this.$t('lobby.pending_players_plural', { n: count })
        },

        canProceed() {
            return this.canProceedPlayers && !this.hasPendingPlayers
        },

        currentValidationMessage() {
            if (this.hasPendingPlayers) {
                return this.pendingPlayersMessage
            }
            return this.playerValidationMessage
        },

        /** The realtime gatekeeper appears the moment the host starts the match. */
        hasMatchStarted() {
            return (this.matchStore.match.gatekeeper || []).length > 0
        },

        /** Joiners get removed from the roster when the host kicks them. */
        isStillInMatch() {
            if (this.isLoadingMatch) return true
            return (this.matchStore.setup.profiles || []).some(
                (p) => p.profile_id === this.authStore.profile?.id,
            )
        },
    },
    watch: {
        hasMatchStarted(started) {
            if (started && !this.isHost) {
                this.goToPlay()
            }
        },
        isStillInMatch(stillIn) {
            if (!stillIn && !this.isHost && !this.isLeaving) {
                this.$toast(
                    this.$t('lobby.removed_title'),
                    5000,
                    this.$t('lobby.removed_body'),
                )
                this.matchStore.clearMatch()
                this.$router.push({ name: 'home' })
            }
        },
        'matchStore.matchEndedRemotely'(info) {
            if (!info || this.isLeaving) return
            this.isLeaving = true
            this.matchStore.clearMatchEndedRemotely()
            this.matchStore.clearMatch()
            this.$toast(this.$t('lobby.ended_title'), 5000, this.$t('lobby.ended_body'))
            this.$router.push({ name: 'home' })
        },
    },
    async mounted() {
        const matchId = this.$route.params.matchId

        if (!matchId) {
            console.error('No match ID in route')
            this.$router.push({ name: 'home' })
            return
        }

        await this.loadMatch(matchId)

        // After loadMatch, so the party size reported is the real one rather than an empty
        // pre-fetch state. Pair with match_started to get the lobby's conversion rate - the
        // single number that says whether online play works.
        this.$analytics.trackEvent(ANALYTICS_EVENTS.LOBBY_OPENED, {
            is_host: this.isHost,
            online: this.matchStore.isRemotePlay,
            player_count: (this.matchStore.setup.profiles || []).length,
        })

        // Match already running (e.g. joiner opened the lobby late): go play.
        if (this.hasMatchStarted && !this.isHost) {
            this.goToPlay()
            return
        }

        const selectSetupId = this.$route.query.selectSetupId
        if (selectSetupId) {
            await this.authStore.fetchSetups()
            await this.$nextTick()
            await this.applySetupSelection(selectSetupId)
            this.$router.replace({
                name: 'match-lobby',
                params: { matchId },
            })
        } else if (!this.matchStore.setup.dart_setup_id) {
            await this.authStore.fetchSetups()
            await this.$nextTick()
            const defaultSetup = this.authStore.dart_setups?.find((s) => s.is_default === true)
            if (defaultSetup) {
                await this.applySetupSelection(defaultSetup.id)
            }
        }
    },
    methods: {
        async loadMatch(matchId) {
            this.isLoadingMatch = true

            try {
                if (
                    this.matchStore.setup.id !== matchId &&
                    this.matchStore.match.id !== matchId
                ) {
                    await this.matchStore.restoreMatchFromAPI(matchId)
                }
                this.initializeTeams()
                this.isLoadingMatch = false
            } catch (error) {
                console.error('Failed to load match:', error)
                this.$toast(this.$t('general.error'), 5000, this.$t('lobby.error_load'))
                this.$router.push({ name: 'home' })
            }
        },

        async handleSetupChange(setupId) {
            await this.applySetupSelection(setupId)
        },

        async applySetupSelection(setupId) {
            try {
                await this.matchStore.updateOwnDartSetup(setupId)
            } catch (error) {
                console.error('Failed to save dart setup:', error)
                this.$toast(this.$t('general.error'), 5000, this.$t('lobby.error_save_setup'))
            }
        },

        playerBadge(matchProfile) {
            if (matchProfile.profile_id === this.matchStore.setup.creator_profile_id) {
                return this.$t('lobby.host_badge')
            }
            return matchProfile.ready ? this.$t('lobby.joined_badge') : this.$t('lobby.invite_sent_badge')
        },

        playerBadgeVariant(matchProfile) {
            const isHost = matchProfile.profile_id === this.matchStore.setup.creator_profile_id
            return isHost || matchProfile.ready ? 'success' : 'neutral'
        },

        playerStatus(matchProfile) {
            if (matchProfile.profile_id === this.matchStore.setup.creator_profile_id) return 'host'
            return matchProfile.ready ? 'joined' : 'invited'
        },

        openExplanation(key) {
            this.$serviceBus.emit('open-game-explanation', {
                key: key,
                infoOnly: true,
            })
        },

        goToPlay() {
            // The realtime snapshot only carries matchData/gatekeeper; fill in
            // the match metadata (as startMatch() does for the host) so the
            // play screen doesn't need a refetch.
            const matchId = this.matchStore.setup.id || this.matchStore.match.id
            this.matchStore.match.id = matchId
            this.matchStore.match.matchtype_id = this.matchStore.setup.matchtype_id
            this.matchStore.match.online = this.matchStore.setup.online
            this.matchStore.match.creator_profile_id = this.matchStore.setup.creator_profile_id

            this.$router.push({
                name: 'match-play',
                params: { matchId },
            })
        },

        async nextStep() {
            if (!this.canProceed || this.isSaving) {
                if (!this.canProceed) {
                    this.showValidation()
                }
                return
            }

            this.hideValidation()
            this.isSaving = true

            // The lobby is where the line-up is settled - PlayerSelectorView's route is no longer
            // navigated to, so this is the only place that sees the final set of players.
            const profiles = this.matchStore.setup.profiles || []
            this.$analytics.trackEvent(ANALYTICS_EVENTS.PLAYERS_SELECTED, {
                player_count: this.readyPlayers.length,
                invited_count: profiles.length - this.readyPlayers.length,
                bot_count: profiles.filter((p) =>
                    this.matchStore.isBotProfile(p.profile_id ?? p.id),
                ).length,
                play_mode: this.matchStore.playMode,
            })

            try {
                await this.matchStore.updateMatch()

                if (this.isSoloGame && !this.isCamelRace) {
                    const soloPlayer = this.readyPlayers[0]
                    this.matchStore.setup.starting_player = soloPlayer.team

                    await this.matchStore.startMatch()
                    this.goToPlay()
                } else {
                    this.$router.push({
                        name: 'match-starter-selector',
                        params: { matchId: this.matchStore.setup.id },
                    })
                }
            } catch (error) {
                console.error('Failed to start match:', error)
                this.$toast(this.$t('general.error'), 5000, this.$t('lobby.error_start'))
                this.isSaving = false
            }
        },

        async goBack() {
            if (this.isLeaving) return
            this.isLeaving = true

            try {
                const matchId = this.matchStore.setup.id

                if (this.isHost) {
                    if (matchId) {
                        await this.matchStore.cancelMatch(matchId)
                    }
                } else if (matchId && this.authStore.profile?.id) {
                    await this.matchStore.leaveMatch(matchId, this.authStore.profile.id)
                }

                this.matchStore.clearMatch()
                this.$router.push({ name: 'home' })
            } catch (error) {
                console.error('Failed to leave lobby:', error)
                this.matchStore.clearMatch()
                this.$router.push({ name: 'home' })
            }
        },

        async deleteLobby() {
            if (this.isDeleting) return
            this.isDeleting = true
            this.isLeaving = true

            try {
                const matchId = this.matchStore.setup.id
                if (matchId) {
                    await this.matchStore.cancelMatch(matchId)
                }
                this.matchStore.clearMatch()
                this.$router.push({ name: 'home' })
            } catch (error) {
                console.error('Failed to delete lobby:', error)
                this.$toast(this.$t('general.error'), 5000, this.$t('lobby.error_delete'))
                this.matchStore.clearMatch()
                this.$router.push({ name: 'home' })
            } finally {
                this.isDeleting = false
                this.showDeleteLobbyModal = false
            }
        },
    },
}
</script>
